import { useState, useEffect } from "react";
import { getApps, initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
} from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

import { CLogger, EnvUtils } from "../../../utils";

import { FirebaseContext, FIREBASE_REGION } from "./context";

const log = CLogger.category("contexts.firebase.FirebaseContext");

export interface FirebaseProviderProps {
  children?: React.ReactNode;
}

/**
 * This context loads initialises Firebase.
 */
export function FirebaseProvider({ children }: FirebaseProviderProps) {
  const [initialised, setInitialised] = useState(false);

  // Initialise Firebase when we have a valid configuration
  useEffect(() => {
    if (!initialised) {
      // Check if Firebase is already initialised
      if (getApps().length > 0) {
        setInitialised(true);
        return;
      }

      try {
        // Parse Firebase config
        const firebaseJSON = EnvUtils.get("FIREBASE_CONFIG");
        const config = JSON.parse(firebaseJSON);

        // Initialise the app
        const app = initializeApp(config);

        // Set emulators if on localhost
        if (window.location.hostname === "localhost") {
          connectAuthEmulator(getAuth(app), "http://localhost:9099");
          connectFirestoreEmulator(getFirestore(app), "localhost", 8080);
          connectFunctionsEmulator(
            getFunctions(app, FIREBASE_REGION),
            "localhost",
            5001
          );
          log({
            level: "info",
            message: `Connected to Firebase Emulators.`,
          });
        }

        setInitialised(true);

        log({
          level: "debug",
          message: `Initialised Firebase.`,
        });
      } catch (error: any) {
        log({
          level: "error",
          message: `Failed to parse Firebase configuration. ${error.message}`,
        });
      }
    }
  }, [initialised, setInitialised]);

  return (
    <FirebaseContext.Provider value={{ initialised }}>
      {children}
    </FirebaseContext.Provider>
  );
}
